import type { EmblaCarouselType } from "embla-carousel";
import { useCallback, useEffect, useState } from "react";
import { ThemeKind } from "src/types";
import styled, { css } from "styled-components";

import { AngleLeft } from "@/components/editor/shared/icons/angle-left";
import { AngleRight } from "@/components/editor/shared/icons/angle-right";
import { darkenColor } from "@/components/editor/utils/colors";
import {
  ColorLabels,
  useInvertedColor,
} from "@/components/editor/utils/use-color";

type UseDotPaginationType = {
  prevBtnDisabled: boolean;
  nextBtnDisabled: boolean;
  onPrevButtonClick: () => void;
  onNextButtonClick: () => void;
  selectedIndex: number;
  scrollSnaps: number[];
  onDotButtonClick: (index: number) => void;
};

export const useEmblaPagination = (
  emblaApi: EmblaCarouselType | undefined
): UseDotPaginationType => {
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const onPrevButtonClick = useCallback(() => {
    emblaApi?.scrollPrev();
  }, [emblaApi]);

  const onNextButtonClick = useCallback(() => {
    emblaApi?.scrollNext();
  }, [emblaApi]);

  const onPrevNextSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) {
      return;
    }

    onPrevNextSelect(emblaApi);
    emblaApi.on("reInit", onPrevNextSelect).on("select", onPrevNextSelect);
  }, [emblaApi, onPrevNextSelect]);

  const onDotButtonClick = useCallback(
    (index: number) => {
      emblaApi?.scrollTo(index);
    },
    [emblaApi]
  );

  const onInit = useCallback((emblaApi: EmblaCarouselType) => {
    setScrollSnaps(emblaApi.scrollSnapList());
  }, []);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, []);

  useEffect(() => {
    if (!emblaApi) {
      return;
    }

    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on("reInit", onInit).on("reInit", onSelect).on("select", onSelect);
  }, [emblaApi, onInit, onSelect]);

  return {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
    selectedIndex,
    scrollSnaps,
    onDotButtonClick,
  };
};

export const DotButton = styled.button<{ $isSelected: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;

  /* theme based styling */

  ${({ theme, $isSelected }) => {
    const themeKind: ThemeKind = theme.themeKind;

    switch (themeKind) {
      case ThemeKind.CROSBY:
        return css`
          background: ${({ theme }) =>
            $isSelected
              ? theme.colors[ColorLabels.PRIMARY_FONT]
              : darkenColor(
                  useInvertedColor(theme.colors[ColorLabels.PRIMARY_FONT]),
                  0.9
                )};
        `;

      default:
        return "";
    }
  }}
`;

const StyledButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  /* theme based styling */

  ${({ theme }) => {
    const themeKind: ThemeKind = theme.themeKind;

    switch (themeKind) {
      case ThemeKind.CROSBY:
        return css`
          background: ${theme.colors[ColorLabels.SECONDARY]};
        `;

      default:
        return "";
    }
  }}

  &:disabled {
    opacity: 0.2;
  }
`;

export function Prev(props: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <StyledButton {...props}>
      <AngleLeft />
    </StyledButton>
  );
}

export function Next(props: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <StyledButton {...props}>
      <AngleRight />
    </StyledButton>
  );
}
